// Transfers (design/screens/stock-transfer.html; UC-INVENTORY-011, -012): the acting branch's transfers out and in, newest first,
// each with its status in words — Draft, In transit, Partially received, Received, Cancelled — and Cancel for the sender; a
// transfer: From (the acting branch) and To (another Active branch of the organisation), who asked, a reason, and lines added by
// scanning a serial or an SKU, each at the sender's weighted average with what Send leaves available; Save draft numbers it on the
// sending branch's sequence and moves nothing; Send moves each unit In transit — still on the sender's hand, out of its available
// (W10) — with no Control PIN; the receiving branch scans the units out of the parcel and receives all or part; the sender may
// cancel a draft, or the units not yet received, which go back into its store. A transfer is versioned: its edit, Send and cancel
// carry its version, and every write its own Idempotency-Key. Send saves what was typed first; refused after that save, the page
// is the saved draft's at its new version and says so, so the corrected Send edits that draft (UC-INVENTORY-011 A8). What was
// typed survives a save and a conflict, and is guarded against a refresh or a closed tab (A9). A scanner's code that lands in a
// draft line's Quantity, or in a received line's Received, is taken as a scan, never as a figure (UC-INVENTORY-011 A10,
// UC-INVENTORY-012 A5); on a tablet the camera reads a code into either scan (TC-INVENTORY-170, -171).
import {
  STOCK_TRANSFER_DIRECTIONS, cents, type Cents, type Problem, type StockLineView, type StockTransferDirection, type TransferLineView, type TransferSummary, type TransferView,
} from "@visionx/contracts";
import { timesQuantity } from "@visionx/money";
import { keepPreviousData, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { newIdempotencyKey } from "../api/client";
import { products } from "../api/endpoints.catalogue";
import { serials, stock, transfers, type TransferLineBody } from "../api/endpoints.inventory";
import { ApiError, NetworkError, carriedOut, describeError, isApiError, isNetworkError } from "../api/problem";
import { formatDate, formatDateTime, formatMoney } from "../lib/format";
import { useOneStore } from "../session/one-store";
import { can, useSessionView } from "../session/store";
import { fieldScanCollector } from "../shell/key-burst";
import { Alert, Checkbox, Empty, ErrorAlert, Field, Loading, Modal, Pager, Panel, Select, TextArea, TextInput } from "../ui/bits";
import { Icon } from "../ui/icons";
import { SERIAL_WORD, andList } from "./CatalogueShared";
import { NoAccessPanel, PageHead } from "./common";
import { useBranchNames, useLeaveGuard } from "./PatientsShared";
import { CameraScan, SerialStatusChip, TransferStatusChip, controlIdText, personWithId, shortName, useActingBranch, useStockWrites } from "./StockShared";

/** A branch's name at the start of a sentence: "The receiving branch receives it" before To is chosen. */
const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);
const isDirection = (v: string | null): v is StockTransferDirection => (STOCK_TRANSFER_DIRECTIONS as readonly string[]).includes(v ?? "");
const etagOf = (t: Pick<TransferSummary, "version">): string => `W/"${t.version}"`;

function noAccess(page: string) {
  return <section className="screen" id="s-stock-transfer"><div className="content"><PageHead title={page} /><NoAccessPanel page={page} permission="inventory.transfer.write" /></div></section>;
}

/** /stock-transfer: the acting branch's transfers out (or in), newest first. */
export function Transfers() {
  const s = useSessionView();
  const qc = useQueryClient();
  const acting = useActingBranch();
  const { online, title } = useStockWrites();
  const [sp, setSp] = useSearchParams();
  const allowed = can(s, "inventory.transfer.write");
  const direction: StockTransferDirection = isDirection(sp.get("direction")) ? (sp.get("direction") as StockTransferDirection) : "outbound";
  const [cursors, setCursors] = useState<string[]>([]);
  useEffect(() => { setCursors([]); }, [direction]);
  const cursor = cursors[cursors.length - 1];
  const q = useQuery({ queryKey: ["transfers", direction, acting.id, cursor ?? ""], queryFn: () => transfers.list({ direction, branchId: acting.id ?? undefined }, { cursor }), enabled: allowed && acting.id !== null, placeholderData: keepPreviousData });
  const [cancelling, setCancelling] = useState<TransferSummary | null>(null);
  const [notice, setNotice] = useState<ReactNode>(null);
  // A practice of one store has nowhere to send stock: its transfers from before stay listed, with Cancel…, but no new one is
  // offered, and the foot says why (UC-INVENTORY-011 A12; DVN's answers of 28 Oct 2026, change 13).
  const oneStore = useOneStore();
  if (!allowed) return noAccess("Transfers");
  const rows = q.data?.data.items ?? [];
  return (
    <section className="screen" id="s-stock-transfers">
      <div className="content">
        <PageHead title="Transfers" sub={oneStore
          // A practice of one store has no branches to transfer between (the verification of 30 Oct 2026, C13-2).
          ? `${acting.name} · no Control PIN — a transfer is not an adjustment`
          : `${acting.name} · between branches of ${s?.organisation.legalName ?? "the organisation"} · no Control PIN — a transfer is not an adjustment`}
          actions={<><Link className="btn" to="/stock">Back to stock</Link>{!oneStore && <Link className={`btn primary${online ? "" : " disabled"}`} to="/stock-transfer/new" aria-disabled={!online || undefined} title={title} onClick={(e) => { if (!online) e.preventDefault(); }}><Icon name="plus" />New transfer</Link>}</>} />
        {notice && <Alert tone="ok" role="status" actions={<button className="btn sm" type="button" onClick={() => setNotice(null)}>Dismiss</button>}>{notice}</Alert>}
        <div className="tabs" role="tablist" aria-label="Direction">
          <a role="tab" aria-selected={direction === "outbound"} className={direction === "outbound" ? "active" : ""} href="/stock-transfer" onClick={(e) => { e.preventDefault(); setSp({}, { replace: true }); }}>Outbound from {acting.name}</a>
          <a role="tab" aria-selected={direction === "inbound"} className={direction === "inbound" ? "active" : ""} href="/stock-transfer?direction=inbound" onClick={(e) => { e.preventDefault(); setSp({ direction: "inbound" }, { replace: true }); }}>Incoming to {acting.name}</a>
        </div>
        {q.isPending && <Loading what="transfers" />}
        {q.isError && <ErrorAlert error={q.error} />}
        {q.data && (
          <Panel title="Transfers" meta={`${acting.name} ${direction === "outbound" ? "outbound" : "incoming"} · newest first`} tight foot={<>
            <span className="help">{oneStore
              ? `${acting.name} is the organisation's only active branch, so there is nowhere to send stock; the transfers above are kept as they were.`
              : <>A transfer is In transit from Send until the receiving branch scans it in; until then its units stay in the sending branch&#39;s on hand.</>}</span>
            {(cursors.length > 0 || q.data.data.nextCursor) && <Pager label={`Page ${cursors.length + 1}`} hasPrev={cursors.length > 0} hasNext={!!q.data.data.nextCursor} onPrev={() => setCursors((c) => c.slice(0, -1))} onNext={() => setCursors((c) => [...c, q.data.data.nextCursor!])} />}
          </>}>
            {rows.length === 0 ? <Empty title={direction === "outbound" ? `No transfers out of ${acting.name} yet.` : `Nothing is on its way to ${acting.name}.`} /> : (
              <div className="tbl-scroll">
                <table>
                  <thead><tr><th>Transfer</th><th>Date</th><th>From → To</th><th className="r">Units</th><th className="r">Value at cost</th><th>Status</th><th></th></tr></thead>
                  <tbody>{rows.map((t) => {
                    const atSender = t.fromBranch.id === acting.id;
                    const cancellable = atSender && (t.status === "Draft" || ((t.status === "Sent" || t.status === "PartiallyReceived") && t.units - t.unitsReceived - t.unitsReturned > 0));
                    return (
                      <tr key={t.id}>
                        <td className="mono nw"><Link to={`/stock-transfer/${t.id}`}>{t.number}</Link></td>
                        <td className="nw">{t.status === "Draft" ? formatDateTime(t.createdAt) : formatDate(t.sentAt ?? t.createdAt)}</td>
                        <td className="nw">{t.fromBranch.name} → {t.toBranch.name}</td>
                        <td className="r num">{t.units}{t.status === "PartiallyReceived" && <div className="small faint nw">{t.unitsReceived} of {t.units} received</div>}</td>
                        <td className="money">{formatMoney(t.totalValueCents)}</td>
                        <td><TransferStatusChip status={t.status} />{(t.receivedAt || t.cancelledAt) && <div className="small faint nw">{t.receivedAt ? formatDate(t.receivedAt) : `cancelled ${formatDate(t.cancelledAt)}`}</div>}</td>
                        <td className="r">{cancellable && <button className="btn sm ghost" type="button" disabled={!online} title={title} onClick={() => setCancelling(t)}>Cancel…</button>}</td>
                      </tr>
                    );
                  })}</tbody>
                </table>
              </div>
            )}
          </Panel>
        )}
        {cancelling && <CancelModal t={cancelling} onClose={() => setCancelling(null)} onDone={(v) => {
          const number = cancelling.number;
          setCancelling(null);
          void qc.invalidateQueries({ queryKey: ["transfers"] });
          void qc.invalidateQueries({ queryKey: ["stock"] });
          if (!v) forgetTransfer(qc, cancelling.id);
          setNotice(v ? <b>{v.number} cancelled.</b> : <><b>{number} cancelled:</b> {UNREAD}</>);
        }} />}
      </div>
    </section>
  );
}

/**
 * A write to a transfer and its answer — or, sent again under its key after a lost answer and carried out with its answer not kept
 * (409 REQUEST_COMPLETED; BR-INVENTORY-025), the transfer read again: the screen goes on as for the write's answer. Null when that
 * read fails as well: the write was carried out and its transfer could not be read, which the screen says — the read's failure is
 * never said as the write's (the verification of round 3's fixes, 25 Oct 2026; TC-INVENTORY-223). Null too for a read that does not
 * show the write (`shows`): when the network fails the device answers a read from its own last copy of the transfer (D13), the
 * transfer as it was before the write (the verification of the closing check's fixes, 25 Oct 2026; UC-INVENTORY-011 A6).
 */
async function orReadAgain(id: string, write: () => Promise<{ data: TransferView }>, shows: (v: TransferView) => boolean): Promise<TransferView | null> {
  try {
    return (await write()).data;
  } catch (err) {
    if (!carriedOut(err)) throw err;
  }
  return readShowing(id, shows);
}
/** The transfer read again, when the read shows what was written (`orReadAgain`); null when it fails or does not. */
async function readShowing(id: string, shows: (v: TransferView) => boolean): Promise<TransferView | null> {
  try {
    const v = (await transfers.get(id)).data;
    return shows(v) ? v : null;
  } catch {
    return null;
  }
}
/** A versioned write — Send, Cancel — is in a read of its transfer once the read is past the version it was sent against. */
const pastVersion = (sentAgainst: number) => (v: TransferView): boolean => v.version > sentAgainst;
/**
 * What a transfer's write carried out says after its subject when its transfer could not be read again (`orReadAgain` answered null).
 * The transfer's own page is not read again then — a read that fails would put its failure in the transfer's place — but what the page
 * holds of it is no longer the transfer: opened again, it is read again (`forgetTransfer`).
 */
const UNREAD = "its answer did not arrive, and the transfer could not be read again — open it again to see it.";
/** A receipt's own words for it (UC-INVENTORY-012 A4): what was received is shown as sent, and read again on the panel. */
const UNREAD_RECEIPT = "its answer did not arrive, and the transfer could not be read again — read it again to see it; nothing is received twice.";

/**
 * A transfer whose write was carried out and that could not be read again (UC-INVENTORY-011 A6, UC-INVENTORY-012 A4; the closing check
 * of 25 Oct 2026, CHKF-fixup5i-1): the transfer as the cache holds it is no longer the transfer, so it is dropped — on no page now, or
 * as its page is left (`useUnreadTransfer`) — and the transfer opened again is read before anything is offered on it, never shown as it
 * was before the write: a receipt entered again on it would go under a new key and be received a second time.
 */
function forgetTransfer(qc: QueryClient, id: string): void {
  void qc.invalidateQueries({ queryKey: ["transfer", id], exact: true, refetchType: "none" });
  setTimeout(() => qc.removeQueries({ queryKey: ["transfer", id], exact: true }), 0);
}

/** The page's half of `forgetTransfer`: marked stale now, the page left as it is in front of the user, and dropped once it is left. */
function useUnreadTransfer(id: string | null): { unread: () => void; read: () => void } {
  const qc = useQueryClient();
  const pending = useRef(false);
  useEffect(() => () => { if (pending.current && id) forgetTransfer(qc, id); }, [qc, id]);
  return {
    unread: () => { if (!id) return; pending.current = true; void qc.invalidateQueries({ queryKey: ["transfer", id], exact: true, refetchType: "none" }); },
    read: () => { pending.current = false; },
  };
}

/** UC-INVENTORY-011 A3–A5: a reason; a sent transfer's units not yet received go back into the sender's store. */
function CancelModal({ t, onClose, onDone }: { t: TransferSummary; onClose: () => void; onDone: (v: TransferView | null) => void }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const key = useMemo(() => newIdempotencyKey(), [reason.trim()]); // eslint-disable-line react-hooks/exhaustive-deps
  const left = t.units - t.unitsReceived - t.unitsReturned;
  const submit = async () => {
    const why = reason.trim();
    if (!why || busy) return;
    setBusy(true);
    setError(null);
    try { onDone(await orReadAgain(t.id, () => transfers.cancel(t.id, why, etagOf(t), key), pastVersion(t.version))); }
    catch (err) { setError(err); }
    finally { setBusy(false); }
  };
  const stale = isApiError(error) && error.code === "VERSION_CONFLICT";
  const reasonErr = isApiError(error) ? error.field("reason")?.message : undefined;
  return (
    <Modal title={`Cancel ${t.number}`} meta={`${t.fromBranch.name} → ${t.toBranch.name}`} onClose={() => { if (!busy) onClose(); }} onSubmit={() => void submit()} footer={<>
      <button className="btn" type="button" onClick={onClose} disabled={busy}>Keep it</button>
      <button className="btn danger" type="submit" disabled={busy || !reason.trim()}>{busy ? "Cancelling…" : t.status === "Draft" ? "Cancel draft" : "Cancel the rest"}</button>
    </>}>
      {error !== null && !reasonErr && <Alert tone="crit"><b>Not cancelled.</b> {stale ? "The transfer changed since it was read; close this and open it again." : describeError(error)}</Alert>}
      {t.status === "Draft"
        ? <p>The draft keeps its number; nothing has moved, so nothing moves back.</p>
        : <p>The {left} unit{left === 1 ? "" : "s"} not yet received go{left === 1 ? "es" : ""} back into {t.fromBranch.name}&#39;s store — on hand unchanged, available again. {t.unitsReceived > 0 ? `${t.unitsReceived} already received at ${t.toBranch.name} stay there.` : ""} A unit truly lost is then written off at {t.fromBranch.name}.</p>}
      <Field label="Reason" required htmlFor="transfer-cancel-reason" error={reasonErr}>
        <TextArea id="transfer-cancel-reason" rows={2} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
      </Field>
    </Modal>
  );
}

// --- one transfer --------------------------------------------------------------------------------------------------------------

interface DraftLine {
  key: string;
  productId: string; sku: string; name: string; trackSerials: boolean;
  serialId: string | null; serialNumber: string | null;
  quantity: string;
}
let draftKey = 0;
/** A saved line keeps its own id as its key, so reading the draft again re-renders its rows without remounting a field being typed in. */
const fromView = (l: TransferLineView): DraftLine => ({ key: l.id, productId: l.productId, sku: l.sku, name: l.productName, trackSerials: l.serialId !== null, serialId: l.serialId, serialNumber: l.serialNumber, quantity: String(l.quantity) });
const bodyLines = (lines: readonly DraftLine[]): TransferLineBody[] =>
  lines.map((l) => (l.serialId ? { serialId: l.serialId } : { productId: l.productId, quantity: Number(l.quantity) }));

/** /stock-transfer/new and /stock-transfer/:id */
export function TransferPage() {
  const s = useSessionView();
  const { id } = useParams();
  const isNew = id === undefined || id === "new";
  const allowed = can(s, "inventory.transfer.write");
  const oneStore = useOneStore();
  const q = useQuery({ queryKey: ["transfer", id], queryFn: () => transfers.get(id!), enabled: allowed && !isNew });
  if (!allowed) return noAccess("Transfers");
  if (isNew && oneStore) return <NowhereToSend />;
  if (isNew) return <TransferForm key="new" view={null} />;
  if (q.isPending) return <section className="screen" id="s-stock-transfer"><div className="content"><Loading what="the transfer" /></div></section>;
  if (q.isError) {
    return <section className="screen" id="s-stock-transfer"><div className="content"><PageHead title="Transfer" actions={<Link className="btn" to="/stock-transfer">Back to transfers</Link>} />
      {isApiError(q.error) && q.error.code === "NOT_FOUND" ? <Alert tone="warn"><b>This transfer was not found.</b> It may be between branches you are not assigned to.</Alert> : <ErrorAlert error={q.error} />}</div></section>;
  }
  // Keyed by the transfer alone: a save moves its version without throwing away what is being typed or what the page says
  // (the review of 18 Oct 2026, D1-5, D1-12).
  return <TransferForm key={q.data.data.id} view={q.data.data} />;
}

/** A new transfer at a practice of one store, opened by its address: there is no other Active branch to send to (UC-INVENTORY-011 A12). */
function NowhereToSend() {
  const acting = useActingBranch();
  return (
    <section className="screen" id="s-stock-transfer">
      <div className="content">
        <PageHead title="New transfer" actions={<Link className="btn" to="/stock-transfer">Back to transfers</Link>} />
        <Alert tone="info"><b>Nothing can be sent: {acting.name || "this branch"} is the organisation&#39;s only active branch.</b> A transfer moves stock to another active branch of the organisation.</Alert>
      </div>
    </section>
  );
}

/** What a new transfer's page hands its saved draft's page: a notice, or a Send refused after the draft was saved (D1-4). */
interface TransferHandoff {
  notice?: string | undefined;
  refused?: { problem: Problem | null; message: string; offline: boolean } | undefined;
}
/** An error as plain data for the address's state, and back again. */
const handoffOf = (err: unknown): NonNullable<TransferHandoff["refused"]> => (isApiError(err)
  ? { problem: { type: "about:blank", title: err.title, status: err.status, code: err.code, ...(err.detail !== undefined ? { detail: err.detail } : {}), errors: err.errors, meta: err.meta, requestId: err.requestId }, message: err.message, offline: false }
  : { problem: null, message: err instanceof Error ? err.message : String(err), offline: isNetworkError(err) && err.offline });
const errorOf = (r: NonNullable<TransferHandoff["refused"]>): unknown => (r.problem ? new ApiError(r.problem) : new NetworkError(r.message, r.offline));

/** A refusal and where it came: a save, or Send's dispatch of a draft that exists (saved, and numbered `number`). */
interface Failure { error: unknown; stage: "save" | "dispatch"; number: string | null }

function TransferForm({ view }: { view: TransferView | null }) {
  const s = useSessionView();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const acting = useActingBranch();
  const { all: branches } = useBranchNames();
  const { online, title } = useStockWrites();
  const draft = view === null || view.status === "Draft";
  const atSender = view === null || view.fromBranch.id === acting.id;
  const atReceiver = view !== null && view.toBranch.id === acting.id;
  const editable = draft && atSender;
  const handoff = (location.state ?? null) as TransferHandoff | null;
  const unreadTransfer = useUnreadTransfer(view?.id ?? null);
  const [to, setTo] = useState(view?.toBranch.id ?? "");
  const [reason, setReason] = useState(view?.reason ?? "");
  const [lines, setLines] = useState<DraftLine[]>(() => (view ? view.lines.map(fromView) : []));
  const [dirty, setDirty] = useState(false);
  const [scan, setScan] = useState("");
  const [scanMsg, setScanMsg] = useState<ReactNode>(null);
  const [choices, setChoices] = useState<StockLineView[] | null>(null);
  const [busy, setBusy] = useState<"save" | "send" | null>(null);
  const [failure, setFailure] = useState<Failure | null>(() => (handoff?.refused && view ? { error: errorOf(handoff.refused), stage: "dispatch", number: view.number } : null));
  const error = failure?.error ?? null;
  const [cancelling, setCancelling] = useState(false);
  const [notice, setNotice] = useState<ReactNode>(() => (handoff?.notice ? <b>{handoff.notice}</b> : null));
  const scanRef = useRef<HTMLInputElement>(null);
  // The handoff is read once; a reload of the page shows the draft as it is.
  useEffect(() => { if (handoff) navigate(location.pathname + location.search, { replace: true, state: null }); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  // The form follows the transfer as the server keeps it while nothing is typed; once something is, what was typed stays — a
  // change made meanwhile by someone else is said, never written over the screen (D1-5; TC-INVENTORY-145).
  const showSaved = (v: TransferView) => { setTo(v.toBranch.id); setReason(v.reason ?? ""); setLines(v.lines.map(fromView)); };
  useEffect(() => { if (view && !dirty) showSaved(view); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [view]);
  // Typed work is guarded against a refresh or a closed tab (D1-10; TC-INVENTORY-149).
  useLeaveGuard(busy !== null || (editable && dirty));
  const fromName = view?.fromBranch.name ?? acting.name;
  const fromCode = view?.fromBranch.code ?? acting.code;
  const toName = branches.find((b) => b.id === to)?.name ?? view?.toBranch.name ?? "the receiving branch";
  const destinations = branches.filter((b) => b.id !== (view?.fromBranch.id ?? acting.id) && (b.status === "Active" || b.id === to));
  const body = { toBranchId: to, reason: reason.trim(), lines: bodyLines(lines) };
  const bodyText = JSON.stringify(body);
  const saveKey = useMemo(() => newIdempotencyKey(), [bodyText]);
  const sendKey = useMemo(() => newIdempotencyKey(), [view?.id, view?.version]); // eslint-disable-line react-hooks/exhaustive-deps
  const change = (f: () => void) => { f(); setDirty(true); };

  // UC-INVENTORY-011 step 2: a serial Available at the branch, else a pack barcode or an SKU of a product held without serials.
  const resolve = async (code: string) => {
    const c = code.trim();
    if (!c) return;
    setScanMsg(null);
    setChoices(null);
    try {
      const found = (await stock.list({ q: c, branchId: acting.id ?? undefined }, { limit: 10 })).data.items;
      const hit = found.length === 1 ? found[0]! : null;
      if (hit?.matchedSerial) {
        const unit = (await serials.list({ q: hit.matchedSerial, branchId: acting.id ?? undefined }, { limit: 2 })).data.items.find((x) => x.serialNumber === hit.matchedSerial);
        if (!unit) { setScanMsg(<><span className="mono">{hit.matchedSerial}</span> is not at {fromName}.</>); return; }
        if (unit.status !== "Available") { setScanMsg(<><span className="mono">{unit.serialNumber}</span> is not available: {SERIAL_WORD[unit.status].toLowerCase()}{unit.status === "Reserved" && unit.reservedOrderId ? ` for ${unit.reservedOrderId}` : ""}.</>); return; }
        if (lines.some((l) => l.serialId === unit.id)) { setScanMsg(<><span className="mono">{unit.serialNumber}</span> is on the transfer already.</>); return; }
        change(() => setLines((ls) => [...ls, { key: `d${++draftKey}`, productId: hit.productId, sku: hit.sku, name: hit.name, trackSerials: true, serialId: unit.id, serialNumber: unit.serialNumber, quantity: "1" }]));
      } else if (hit) add(hit);
      else if (found.length > 1) setChoices(found);
      else setScanMsg(<>Nothing at {fromName} matches <span className="mono">{c}</span>.</>);
      setScan("");
    } catch (err) { setScanMsg(describeError(err)); }
    finally { scanRef.current?.focus(); }
  };
  /** A code read by the scanner — in the scan box, or landed in a line's Quantity (A10) — or by the camera, looked up as a scan. */
  const scanned = (code: string) => { setScan(code); void resolve(code); };
  const add = (l: StockLineView) => {
    if (l.trackSerials) { setScanMsg(<>{l.name} moves serial by serial: scan the frame&#39;s serial.</>); return; }
    change(() => setLines((ls) => (ls.some((x) => x.productId === l.productId && !x.serialId)
      ? ls.map((x) => (x.productId === l.productId && !x.serialId ? { ...x, quantity: String((Number(x.quantity) || 0) + 1) } : x))
      : [...ls, { key: `d${++draftKey}`, productId: l.productId, sku: l.sku, name: l.name, trackSerials: false, serialId: null, serialNumber: null, quantity: "1" }])));
  };

  /** The draft as the server now keeps it, in the cache the page reads: its version is the one the next save or Send carries. */
  const record = (v: TransferView) => {
    qc.setQueryData(["transfer", v.id], (old: unknown) => (old ? { ...(old as object), data: v, etag: etagOf(v) } : { data: v, status: 200, etag: etagOf(v), requestId: "", headers: new Headers() }));
    void qc.invalidateQueries({ queryKey: ["transfers"] });
  };
  /** A version conflict: the transfer as it is now is read again, and what was typed stays on the screen to be saved over it. */
  const conflicted = (err: unknown) => { if (isApiError(err) && err.code === "VERSION_CONFLICT" && view) void qc.invalidateQueries({ queryKey: ["transfer", view.id] }); };
  const save = async (): Promise<TransferView> => {
    if (view && !dirty) return view;
    const r = view ? await transfers.patch(view.id, body, etagOf(view)) : await transfers.save(body, saveKey);
    return r.data;
  };
  const onSave = async () => {
    if (busy) return;
    setBusy("save");
    setFailure(null);
    try {
      const v = await save();
      setDirty(false);
      record(v);
      const words = `${v.number} saved as a draft. Nothing moves until Send.`;
      // A new transfer moves to its draft's page, which says so (D1-12; TC-INVENTORY-146).
      if (!view) navigate(`/stock-transfer/${v.id}`, { replace: true, state: { notice: words } satisfies TransferHandoff });
      else setNotice(<b>{words}</b>);
    } catch (err) { setFailure({ error: err, stage: "save", number: null }); conflicted(err); }
    finally { setBusy(null); }
  };
  const onSend = async () => {
    if (busy) return;
    setBusy("send");
    setFailure(null);
    setNotice(null);
    let saved: TransferView | null = null;
    try {
      const v = await save();
      saved = v;
      // Send saves what was typed first; the saved draft — its number, its version — is kept at once, so a Send refused after it
      // is corrected on that draft, never saved a second time (the review of 18 Oct 2026, D1-4, D1-5; TC-INVENTORY-144, -145).
      if (v !== view) { setDirty(false); record(v); }
      const sent = await orReadAgain(v.id, () => transfers.dispatch(v.id, etagOf(v), v === view ? sendKey : newIdempotencyKey()), pastVersion(v.version));
      void qc.invalidateQueries({ queryKey: ["stock"] });
      // Sent, its answer not kept and the transfer not read again: said so (UC-INVENTORY-011 A6; TC-INVENTORY-223).
      if (!sent) {
        void qc.invalidateQueries({ queryKey: ["transfers"] });
        if (!view) {
          // The draft's page, opened now, reads the transfer: the draft the cache holds was sent.
          qc.removeQueries({ queryKey: ["transfer", v.id], exact: true });
          navigate(`/stock-transfer/${v.id}`, { replace: true, state: { notice: `${v.number} was sent: ${UNREAD}` } satisfies TransferHandoff });
        } else { unreadTransfer.unread(); setNotice(<><b>{v.number} was sent:</b> {UNREAD}</>); }
        return;
      }
      record(sent);
      if (!view) navigate(`/stock-transfer/${sent.id}`, { replace: true });
    } catch (err) {
      if (saved === null) { setFailure({ error: err, stage: "save", number: null }); conflicted(err); return; }
      // A new transfer's Send refused once its draft was saved: the page moves to that draft and says what happened there.
      if (!view) { navigate(`/stock-transfer/${saved.id}`, { replace: true, state: { refused: handoffOf(err) } satisfies TransferHandoff }); return; }
      setFailure({ error: err, stage: "dispatch", number: saved.number });
      conflicted(err);
    } finally { setBusy(null); }
  };
  /** After a conflict: what was typed is dropped and the transfer is shown as it is now. */
  const discard = () => { if (!view) return; showSaved(view); setDirty(false); setFailure(null); };

  const fieldErr = (path: string) => (isApiError(error) ? error.field(path) : undefined);
  const toErr = fieldErr("toBranchId");
  const reasonErr = fieldErr("reason");
  const lineRefusal = (l: DraftLine, i: number): string | null => {
    if (!isApiError(error)) return null;
    const m = error.meta;
    if (error.code === "SERIAL_UNAVAILABLE" && m.serialId === l.serialId) return `${l.serialNumber} is not available: ${typeof m.status === "string" && m.status in SERIAL_WORD ? SERIAL_WORD[m.status as keyof typeof SERIAL_WORD].toLowerCase() : "not in store"}`;
    if (error.code === "INSUFFICIENT_STOCK" && m.productId === l.productId && !l.serialId) return `Only ${String(m.available ?? 0)} available at ${fromName}`;
    const f = error.errors.find((e) => e.path.startsWith(`lines.${i}.`) || e.path === `lines.${i}`);
    return f ? (f.path.endsWith("quantity") ? "A whole number from 1 to 9 999" : f.message) : null;
  };
  const placed = !!toErr || !!reasonErr || lines.some((l, i) => lineRefusal(l, i) !== null);
  const status = view?.status ?? "Draft";
  const totalUnits = lines.reduce((n, l) => n + (Number(l.quantity) || 0), 0);
  const created = view ? `created ${formatDateTime(view.createdAt)} by ${personWithId(view.createdBy)}` : "not saved yet";
  const sub = !view ? `${fromName} → ${to ? toName : "…"} · ${created} · no stock movement until Send`
    : view.status === "Draft" ? `${view.fromBranch.name} → ${view.toBranch.name} · ${created} · not sent — no stock movement until Send`
      : view.status === "Cancelled" ? `${view.fromBranch.name} → ${view.toBranch.name} · cancelled ${formatDateTime(view.cancelledAt)} by ${personWithId(view.cancelledBy)} · ${view.cancelReason ?? ""}`
        : `${view.fromBranch.name} → ${view.toBranch.name} · sent ${formatDateTime(view.sentAt)} by ${personWithId(view.sentBy)}${view.receivedAt ? ` · received ${formatDateTime(view.receivedAt)} by ${personWithId(view.receivedBy)}` : ""}`;
  const canSend = editable && lines.length > 0 && to !== "" && reason.trim() !== "";
  return (
    <section className="screen" id="s-stock-transfer">
      <div className="content">
        <PageHead
          title={<span className="row c gap-8">{view ? <>Transfer <span className="mono">{view.number}</span></> : "New transfer"}<TransferStatusChip status={status} /></span>}
          sub={sub}
          actions={<>
            <Link className="btn" to="/stock-transfer">Back to transfers</Link>
            {view && atSender && (view.status === "Draft" || ((view.status === "Sent" || view.status === "PartiallyReceived") && view.units - view.unitsReceived - view.unitsReturned > 0)) &&
              <button className="btn" type="button" disabled={!online || busy !== null} title={title} onClick={() => setCancelling(true)}>{view.status === "Draft" ? "Cancel draft…" : "Cancel the rest…"}</button>}
            {editable && <button className="btn" type="button" disabled={!online || busy !== null} title={title} onClick={() => void onSave()}>{busy === "save" ? "Saving…" : "Save draft"}</button>}
            {editable && <button className="btn primary" type="button" disabled={!online || busy !== null || !canSend} title={title ?? (canSend ? undefined : "Choose the branch, give the reason and add a line")} onClick={() => void onSend()}>{busy === "send" ? "Sending…" : "Send"}</button>}
          </>}
        />
        {notice && <Alert tone="ok" role="status" actions={<button className="btn sm" type="button" onClick={() => setNotice(null)}>Dismiss</button>}>{notice}</Alert>}
        {failure && <FailureAlert failure={failure} placed={placed} kept={dirty} fromName={fromName} onDiscard={discard} />}
        <div className="split">
          <div className="stack" style={{ gap: 16 }}>
            <Panel title="Transfer details" meta={`between branches of ${s?.organisation.legalName ?? "the organisation"} · same legal entity, no bill`}>
              <div className="row">
                <Field label="From" htmlFor="transfer-from"><div className="input disabled" id="transfer-from">{fromName} · <span className="mono">{fromCode}</span></div></Field>
                <Field label="To" required htmlFor="transfer-to" error={toErr ? (toErr.code === "same_branch" ? "Another branch than the sender" : toErr.code === "inactive" ? "That branch is inactive" : toErr.message) : undefined}>
                  {editable
                    ? <Select id="transfer-to" value={to} onChange={(e) => { const v = e.target.value; change(() => setTo(v)); }}>
                        <option value="">Choose a branch</option>
                        {destinations.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                      </Select>
                    : <div className="input disabled" id="transfer-to">{view?.toBranch.name} · <span className="mono">{view?.toBranch.code}</span></div>}
                </Field>
                <Field label="Requested by" htmlFor="transfer-by"><div className="input disabled" id="transfer-by">{view?.createdBy ? <>{shortName(view.createdBy.displayName)} · <span className="mono">{controlIdText(view.createdBy.controlId)}</span></> : <>{shortName(s?.user.displayName)} · <span className="mono">{s?.user.controlIdDisplay}</span></>}</div></Field>
              </div>
              <Field label="Reason" required htmlFor="transfer-reason" error={reasonErr ? "Give the reason, at most 500 characters" : undefined}>
                {editable ? <TextArea id="transfer-reason" rows={2} maxLength={500} value={reason} onChange={(e) => { const v = e.target.value; change(() => setReason(v)); }} /> : <div className="input disabled ta" id="transfer-reason">{view?.reason}</div>}
              </Field>
            </Panel>
            {view && !draft && atReceiver
              ? <ReceivePanel view={view} onUnread={() => unreadTransfer.unread()} onReceived={(v, words, o) => {
                  // The transfer as received — or, carried out and not read again, left as shown — a read now could fail in its place —
                  // and no longer the transfer to the cache (`useUnreadTransfer`). The page's own read of it, once it shows the receipt,
                  // is already in the cache (`o.read`).
                  if (v) {
                    unreadTransfer.read();
                    if (!o?.read) { qc.setQueryData(["transfer", v.id], (old: unknown) => (old ? { ...(old as object), data: v } : old)); void qc.invalidateQueries({ queryKey: ["transfer", v.id] }); }
                  } else unreadTransfer.unread();
                  void qc.invalidateQueries({ queryKey: ["transfers"] }); void qc.invalidateQueries({ queryKey: ["stock"] }); setNotice(words);
                }} />
              : (
                <Panel title="Lines" meta={<>{lines.length} line{lines.length === 1 ? "" : "s"} · {totalUnits} unit{totalUnits === 1 ? "" : "s"}{view && !dirty ? <> · <span className="mono">{formatMoney(view.totalValueCents)}</span> {draft ? "at weighted average cost" : "at the cost carried from Send"}</> : " at weighted average cost"}</>}>
                  {editable && (
                    <div className="stack" style={{ gap: 8 }}>
                      <div className="row c gap-8">
                        <div className="input w-scan">
                          <Icon name="barcode" />
                          <input ref={scanRef} aria-label="Scan a serial or SKU to add a line" placeholder="Scan a serial or SKU to add a line…" autoComplete="off" maxLength={100} value={scan}
                            onChange={(e) => setScan(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void resolve(scan); } }} />
                        </div>
                        <CameraScan testId="transfer-camera" onCode={scanned} onMiss={(words) => setScanMsg(words)} />
                      </div>
                      {scanMsg && <div className="error" role="alert">{scanMsg}</div>}
                      {choices && <div className="list bordered">{choices.map((c) => <div key={c.productId} className="item"><div className="grow"><span className="mono">{c.sku}</span> {c.name} <span className="small faint">· {c.available} available</span></div><button className="btn sm" type="button" onClick={() => { setChoices(null); add(c); setScan(""); }}>Add</button></div>)}</div>}
                    </div>
                  )}
                  <LinesTable view={view} lines={lines} editable={editable} fromBranchId={view?.fromBranch.id ?? acting.id} fromName={fromName} toName={toName} refusal={lineRefusal}
                    onQuantity={(k, v) => change(() => setLines((ls) => ls.map((x) => (x.key === k ? { ...x, quantity: v } : x))))}
                    onRemove={(k) => change(() => setLines((ls) => ls.filter((x) => x.key !== k)))} onScan={scanned} />
                </Panel>
              )}
          </div>
          <Panel title="What Send does">
            <div className="list">
              <div className="item"><span className="t">1</span><div className="grow"><div className="primary">Serial moves to In transit</div><div className="sub">stock movement &quot;Transferred out&quot; at {fromName} · stays in {fromName}&#39;s on hand, In transit, until {toName} receives it</div></div></div>
              <div className="item"><span className="t">2</span><div className="grow"><div className="primary">{cap(toName)} receives it</div><div className="sub">it waits on {toName}&#39;s incoming transfers · received by scanning the serial · the transfer becomes Received · the unit leaves {fromName}&#39;s on hand and joins {toName}&#39;s</div></div></div>
              <div className="item"><span className="t">3</span><div className="grow"><div className="primary">Cost carries over</div><div className="sub">{fromName}&#39;s weighted average at Send · {toName} sells at its own price; a serial&#39;s own price travels with it</div></div></div>
              <div className="item"><span className="t">4</span><div className="grow"><div className="primary">Nothing posts to Zoho Books</div><div className="sub">stock levels are not synced · same legal entity, no bill</div></div></div>
              <div className="item"><span className="t">5</span><div className="grow"><div className="primary">Recorded in the audit log</div><div className="sub">no Control PIN — a transfer is not an adjustment</div></div></div>
            </div>
          </Panel>
        </div>
        {cancelling && view && <CancelModal t={view} onClose={() => setCancelling(false)} onDone={(v) => {
          setCancelling(false);
          void qc.invalidateQueries({ queryKey: ["transfers"] });
          void qc.invalidateQueries({ queryKey: ["stock"] });
          // Cancelled, its answer not kept and the transfer not read again: said so, the page left as shown (TC-INVENTORY-223).
          if (!v) { unreadTransfer.unread(); setNotice(<><b>{view.number} cancelled:</b> {UNREAD}</>); return; }
          qc.setQueryData(["transfer", v.id], (old: unknown) => (old ? { ...(old as object), data: v } : old));
          void qc.invalidateQueries({ queryKey: ["transfer", v.id] });
        }} />}
      </div>
    </section>
  );
}

/**
 * What a refused save or Send did, in words. A Send refused once its draft exists says the draft is saved and was not sent, its
 * reasons under their lines when they have one (D1-4); a version conflict says what was typed is kept, to be saved over the
 * transfer as it is now, or dropped (D1-5). A Send the sending branch's opening posting holds names that count, with a link, and
 * says the draft is kept (UC-INVENTORY-011 E13); "no longer a draft" is said only when the transfer's own status refuses Send —
 * its machine names the status it is in (`meta.from`) — and any other refusal in the server's words (the third review, PWA-1).
 */
function FailureAlert({ failure, placed, kept, fromName, onDiscard }: { failure: Failure; placed: boolean; kept: boolean; fromName: string; onDiscard: () => void }) {
  const { error, stage, number } = failure;
  // A new transfer's draft saved again under its key after a lost answer, answered as carried out: saved, its number not known here.
  if (stage === "save" && carriedOut(error)) {
    return <Alert tone="ok" role="status"><b>The draft was saved: its answer did not arrive.</b> It is listed under <Link to="/stock-transfer">Transfers</Link>: open it there to send it.</Alert>;
  }
  const stale = isApiError(error) && error.code === "VERSION_CONFLICT";
  const illegal = isApiError(error) && error.code === "ILLEGAL_TRANSITION";
  const meta = isApiError(error) ? error.meta : {};
  const posting = typeof meta.stockTakeId === "string"
    ? <Link className="mono" to={`/stock-take/${meta.stockTakeId}`}>{String(meta.stockTakeNumber ?? "")}</Link>
    : <span className="mono">{String(meta.stockTakeNumber ?? "")}</span>;
  const why = stale
    ? <>The transfer changed since you opened it. {kept ? <>Your changes are kept: Save draft or Send again to apply them to it as it is now, or <button className="linkbtn" type="button" onClick={onDiscard}>show it as it is now</button>.</> : "It is shown as it is now."}</>
    : illegal && meta.reason === "opening_posting" ? <>{posting} is posting {fromName}&#39;s opening stock: nothing leaves {fromName} until that posting ends. The draft is kept; send it then.</>
      : illegal && typeof meta.from === "string" ? "The transfer is no longer a draft."
        : placed ? "Correct what is marked below and send it again." : describeError(error);
  if (stage === "dispatch" && number) {
    return <Alert tone="warn" role="alert"><b>{number} is saved as a draft and was not sent.</b> {why}</Alert>;
  }
  if (placed && !stale) return null;
  return <Alert tone="crit"><b>{stage === "dispatch" ? "Not sent." : "Not saved."}</b> {why}</Alert>;
}

/**
 * The sender's figures for a line's product, read once: its stock line and the product (its supplier and shelf price). A saved line
 * keeps the SKU it was added with (BR-INVENTORY-006) and the stock list finds a product by its SKU as it is now, so a line whose SKU
 * finds nothing is looked for again under the product's current one (TC-INVENTORY-180).
 */
function useLineFigures(branchId: string | null, productId: string, sku: string) {
  const s = useSessionView();
  const qc = useQueryClient();
  const readable = can(s, "catalogue.product.read");
  const readProduct = () => products.get(productId);
  const line = useQuery({
    queryKey: ["stock", "line", branchId, productId],
    queryFn: async () => {
      const find = async (q: string) => (await stock.list({ q, branchId: branchId ?? undefined }, { limit: 20 })).data.items.find((x) => x.productId === productId) ?? null;
      const hit = await find(sku);
      if (hit || !readable) return hit;
      const now = (await qc.fetchQuery({ queryKey: ["product", productId], queryFn: readProduct, staleTime: 60_000 })).data.sku;
      return now === sku ? null : find(now);
    },
    enabled: branchId !== null, staleTime: 15_000,
  });
  const product = useQuery({ queryKey: ["product", productId], queryFn: readProduct, enabled: readable, staleTime: 60_000 });
  return { line: line.data ?? null, product: product.data?.data ?? null };
}

function LinesTable({ view, lines, editable, fromBranchId, fromName, toName, refusal, onQuantity, onRemove, onScan }: {
  view: TransferView | null; lines: DraftLine[]; editable: boolean; fromBranchId: string | null; fromName: string; toName: string;
  refusal: (l: DraftLine, i: number) => string | null; onQuantity: (key: string, v: string) => void; onRemove: (key: string) => void; onScan: (code: string) => void;
}) {
  const saved = (l: DraftLine): TransferLineView | undefined => view?.lines.find((x) => (l.serialId ? x.serialId === l.serialId : x.productId === l.productId && !x.serialId));
  if (lines.length === 0) return <Empty title="No lines yet.">Scan a serial, or a pack&#39;s barcode or SKU, to add it.</Empty>;
  return (
    <div className="tbl-scroll">
      <table>
        <thead><tr><th>Line</th><th>Product</th><th className="serial-col">Serial</th><th>Status at {fromName}</th><th className="r">Qty</th><th className="r">Cost (wtd avg)</th><th className="r">Value</th><th></th></tr></thead>
        <tbody>{lines.map((l, i) => <LineRow key={l.key} n={i + 1} l={l} saved={saved(l)} editable={editable} fromBranchId={fromBranchId} fromName={fromName} toName={toName} refusal={refusal(l, i)} onQuantity={onQuantity} onRemove={onRemove} onScan={onScan} />)}</tbody>
        <tfoot><tr><td></td><td>{lines.length} line{lines.length === 1 ? "" : "s"}</td><td className="serial-col"></td><td></td><td className="r num">{lines.reduce((n, l) => n + (Number(l.quantity) || 0), 0)}</td><td></td><td className="money">{view && view.status !== "Draft" ? formatMoney(view.totalValueCents) : ""}</td><td></td></tr></tfoot>
      </table>
    </div>
  );
}

function LineRow({ n, l, saved, editable, fromBranchId, fromName, toName, refusal, onQuantity, onRemove, onScan }: {
  n: number; l: DraftLine; saved: TransferLineView | undefined; editable: boolean; fromBranchId: string | null; fromName: string; toName: string; refusal: string | null;
  onQuantity: (key: string, v: string) => void; onRemove: (key: string) => void; onScan: (code: string) => void;
}) {
  const f = useLineFigures(editable ? fromBranchId : null, l.productId, l.sku);
  // A scanner's code that lands in the Quantity is a scan (UC-INVENTORY-011 A10): the box goes back to what it held before the code
  // began and the code is looked up as if scanned into the scan box. A person's typing acts as ever (TC-INVENTORY-170).
  const [collector] = useState(fieldScanCollector);
  const quantityKeys = (e: KeyboardEvent<HTMLInputElement>) => {
    const hit = collector.take(e.nativeEvent, e.currentTarget.value);
    if (!hit) return;
    e.preventDefault();
    onQuantity(l.key, hit.before);
    onScan(hit.code);
  };
  // A quantity as typed; anything but up to six digits is none until it is (the server answers the range, BR-INVENTORY-027).
  const qty = /^\d{1,6}$/.test(l.quantity.trim()) ? Number(l.quantity.trim()) : 0;
  const unit = saved?.costSnapshotted ? saved.unitCostCents : f.line?.averageCostCents ?? saved?.unitCostCents ?? null;
  const value: Cents | null = unit === null ? null : timesQuantity(cents(unit), qty);
  const price = f.product ? f.product.atBranch?.priceCents ?? f.product.priceCents : null;
  const supplier = f.product?.supplier?.name ?? saved?.supplierName ?? null;
  const sentState = saved && saved.costSnapshotted
    ? saved.quantityReceived >= saved.quantity ? <span className="chip ok"><span className="dot" />Received</span>
      : saved.quantityReturned > 0 && saved.quantityInTransit === 0 ? <span className="chip neutral">Back in store</span> : <SerialStatusChip status="InTransit" />
    : l.serialId ? <SerialStatusChip status={saved?.serialStatus ?? "Available"} /> : <span className="chip ok"><span className="dot" />In store</span>;
  const warning = editable && f.line ? availabilityWarning(f.line, qty, fromName, toName) : null;
  return (
    <>
      <tr>
        <td className="faint num">{n}</td>
        <td className="product">{l.name}<div className="small faint">SKU <span className="mono nw">{l.sku}</span>{supplier ? ` · ${supplier}` : ""}{price !== null ? <> · shelf price <span className="mono nw">{formatMoney(price)}</span></> : null}</div>
          {l.serialNumber && <div className="small faint serial-sub">Serial <span className="mono nw">{l.serialNumber}</span></div>}{refusal && <div className="error" role="alert">{refusal}</div>}</td>
        <td className="mono nw serial-col">{l.serialNumber ?? <span className="faint">—</span>}</td>
        <td>{sentState}{saved && saved.quantity > 1 && saved.costSnapshotted && <div className="small faint nw">{saved.quantityReceived} of {saved.quantity} received</div>}</td>
        <td className="r num">{editable && !l.serialId ? <TextInput aria-label={`Quantity of ${l.sku}`} boxClass="w-80" inputMode="numeric" value={l.quantity} onChange={(e) => onQuantity(l.key, e.target.value)} onKeyDown={quantityKeys} /> : qty}</td>
        <td className="money">{formatMoney(unit)}</td>
        <td className="money">{formatMoney(value)}</td>
        <td className="r">{editable && <button className="btn sm ghost" type="button" onClick={() => onRemove(l.key)}>Remove</button>}</td>
      </tr>
      {/* The warning spans the product to the value; the last cell stands for the serial's column, which is left out where it is
          narrow, so the row never makes a column of its own (the hands-on test of 21 Oct 2026). */}
      {warning && <tr className="warnline"><td></td><td colSpan={6}><Alert tone="warn">{warning}</Alert></td><td className="serial-col"></td></tr>}
    </>
  );
}

/**
 * UC-INVENTORY-011 step 4: what Send leaves at the sender — "Menlyn will hold 0 available of FR-GG0027O-HAV-50 after Send — still 2 on
 * hand: 1 reserved for SO-MEN-001198 and 1 in transit until Centurion receives it — re-order level 2, already flagged Re-order on
 * Stock." — shown when Send leaves nothing available or takes the line to its re-order level.
 */
export function availabilityWarning(l: StockLineView, qty: number, fromName: string, toName: string): ReactNode {
  const after = l.available - qty;
  const flagsAfter = l.reOrderLevel > 0 && after <= l.reOrderLevel;
  if (after > 0 && !flagsAfter) return null;
  const orders = l.holds.filter((h) => h.kind === "Reserved").map((h) => h.document?.number).filter((x): x is string => !!x);
  const held = [
    after > 0 ? `${after} in store` : null,
    l.reserved > 0 ? `${l.reserved} reserved${orders.length ? ` for ${andList(orders)}` : ""}` : null,
    l.atLab > 0 ? `${l.atLab} at lab` : null,
    l.damaged > 0 ? `${l.damaged} damaged` : null,
    `${l.inTransit + qty} in transit until ${toName} receives ${l.inTransit + qty === 1 ? "it" : "them"}`,
  ].filter((x): x is string => x !== null);
  return <>{fromName} will hold <b>{Math.max(after, 0)} available</b> of <span className="mono">{l.sku}</span> after Send — still {l.onHand} on hand: {andList(held)}{l.reOrderLevel > 0 ? <> — re-order level {l.reOrderLevel}, {l.belowReOrder ? <>already flagged <b>Re-order</b> on Stock</> : <>to be flagged <b>Re-order</b> on Stock</>}</> : null}.</>;
}

/**
 * A receipt sent whose outcome no read of the transfer has shown yet (UC-INVENTORY-012 A4): its body and key, the units each of its
 * lines will have received once it has landed, and what it received of what was in transit. Carried out — 409 REQUEST_COMPLETED — it has
 * landed and waits for a read of its transfer; its answer lost, it may not have landed, and the same units ticked again are the same
 * request. Kept on this device, its page left and the transfer opened again included, until a read shows it or an answer comes.
 */
interface PendingReceipt {
  bodyText: string; key: string;
  expect: Record<string, number>;
  ticks: Record<string, number>; ticked: number; inTransit: number;
  landed: boolean;
}
/** The receipts pending on this device, by user and transfer — one store a query client, so each test's client starts with none. */
const pendingReceipts = new WeakMap<QueryClient, Map<string, PendingReceipt>>();
const pendingOf = (qc: QueryClient): Map<string, PendingReceipt> => {
  let m = pendingReceipts.get(qc);
  if (!m) { m = new Map(); pendingReceipts.set(qc, m); }
  return m;
};
/**
 * Whether a read of the transfer shows the receipt: each of its lines has received at least what it will have once the receipt has
 * landed — never true of the device's own copy of the transfer as it was before it (D13), whatever else moved it meanwhile.
 */
const showsReceipt = (v: TransferView, p: Pick<PendingReceipt, "expect">): boolean =>
  Object.entries(p.expect).every(([lineId, n]) => (v.lines.find((l) => l.id === lineId)?.quantityReceived ?? -1) >= n);
/** A4: a read that answered the transfer as it was before the receipt — the device's own copy — said as such. */
const STALE_READ = { staleRead: true } as const;

/**
 * UC-INVENTORY-012: the units ticked as they come out of the parcel — by a scan, or by hand — and Receive, all or part. A receipt
 * carried out whose transfer could not be read again (A4) keeps what was received, as it was sent, and takes no other until a read of
 * the transfer shows it: entered again, the same units would go under a new key and be received a second time. A receipt whose answer
 * was lost keeps its units ticked and its key until a read shows it, its page left and the transfer opened again included, so the same
 * units ticked again are the same request (the verification of the closing check's fixes, 25 Oct 2026; TC-INVENTORY-233, -234).
 */
function ReceivePanel({ view, onReceived, onUnread }: {
  view: TransferView;
  onReceived: (v: TransferView | null, words: ReactNode, o?: { read?: boolean }) => void;
  onUnread: () => void;
}) {
  const qc = useQueryClient();
  const s = useSessionView();
  const slot = `${s?.user.id ?? ""}:${view.id}`;
  const { online, title } = useStockWrites();
  const [pending, setPending] = useState<PendingReceipt | null>(() => pendingOf(qc).get(slot) ?? null);
  const keep = (p: PendingReceipt | null) => { if (p) pendingOf(qc).set(slot, p); else pendingOf(qc).delete(slot); setPending(p); };
  const unread = pending?.landed === true;
  const open = (view.status === "Sent" || view.status === "PartiallyReceived") && !unread;
  const [ticks, setTicks] = useState<Record<string, number>>(() => pending?.ticks ?? {});
  const [scan, setScan] = useState("");
  const [msg, setMsg] = useState<ReactNode>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const scanRef = useRef<HTMLInputElement>(null);
  const waiting = view.lines.filter((l) => l.quantityInTransit > 0);
  const inTransit = waiting.reduce((n, l) => n + l.quantityInTransit, 0);
  const ticked = Object.values(ticks).reduce((n, x) => n + x, 0);
  const body = waiting.filter((l) => (ticks[l.id] ?? 0) > 0).map((l) => (l.serialId ? { lineId: l.id, serialIds: [l.serialId] } : { lineId: l.id, quantity: ticks[l.id]! }));
  const bodyText = JSON.stringify(body);
  const fresh = useMemo(() => newIdempotencyKey(), [bodyText]);
  // A receipt whose answer was lost is the same request when the same units are ticked again: its key, kept on the device.
  const key = pending && !pending.landed && pending.bodyText === bodyText ? pending.key : fresh;
  /** A unit scanned out of the parcel, ticked on its line; `base` the ticks to tick from when a box was put back first (A5). */
  const onScan = (code: string, base: Record<string, number> = ticks) => {
    const c = code.trim();
    setScan("");
    if (!c) return;
    const bySerial = view.lines.find((l) => l.serialNumber === c);
    // A quantity line by its SKU or by one of its product's barcodes — a box's EAN (A3; the review of 18 Oct 2026, TC-INVENTORY-153).
    // Of several lines of the product, the first with a unit still to tick.
    const packs = view.lines.filter((l) => !l.serialId && (l.sku.toLowerCase() === c.toLowerCase() || l.barcodes.includes(c)));
    const byPack = packs.find((l) => l.quantityInTransit > 0 && (base[l.id] ?? 0) < l.quantityInTransit) ?? packs.find((l) => l.quantityInTransit > 0) ?? packs[0];
    const l = bySerial ?? byPack;
    if (!l) setMsg(<><span className="mono">{c}</span> is not on this transfer</>);
    else if (l.quantityInTransit === 0) setMsg(<><span className="mono">{c}</span> is already received</>);
    else if ((base[l.id] ?? 0) >= l.quantityInTransit) setMsg(<>{l.serialId ? <><span className="mono">{c}</span> is ticked already</> : `All ${l.quantityInTransit} of ${l.sku} are ticked`}</>);
    else { setTicks({ ...base, [l.id]: (base[l.id] ?? 0) + 1 }); setMsg(<>{l.productName}{l.serialNumber ? <> <span className="mono">{l.serialNumber}</span></> : ""} · ticked received</>); }
    scanRef.current?.focus();
  };
  // A scanner's code that lands in a line's Received — the cursor left there after typing a figure — is a scan (A5): the box goes
  // back to what it held before the code began, and the code ticks its unit as if scanned into the scan box; the digits are never
  // taken as a figure, which would tick every unit in transit at once. A person's typing acts as ever (TC-INVENTORY-170).
  const collectors = useRef(new Map<string, ReturnType<typeof fieldScanCollector>>());
  const clampTo = (l: TransferLineView, text: string) => Math.max(0, Math.min(l.quantityInTransit, Number(text) || 0));
  const receivedKeys = (l: TransferLineView) => (e: KeyboardEvent<HTMLInputElement>) => {
    const c = collectors.current.get(l.id) ?? fieldScanCollector();
    collectors.current.set(l.id, c);
    const hit = c.take(e.nativeEvent, e.currentTarget.value);
    if (!hit) return;
    e.preventDefault();
    const base = { ...ticks, [l.id]: clampTo(l, hit.before) };
    setTicks(base);
    onScan(hit.code, base);
  };
  /** The receipt in the transfer as read or answered: the panel takes the next, the notice worded by the receipt itself (A4; V6IS-6). */
  const received = (v: TransferView, p: Pick<PendingReceipt, "ticked" | "inTransit">, o?: { read?: boolean }) => {
    keep(null);
    setTicks({});
    setError(null);
    onReceived(v, <b>{v.status === "Received" ? `${v.number} received.` : `${p.ticked} of ${p.inTransit} received; the rest stays in transit at ${v.fromBranch.name}.`}</b>, o);
  };
  // Whatever reads the transfer as it stands — Read it again, the page read again as the device comes back online, the transfer opened
  // again — ends a pending receipt once the read shows it, worded by the receipt: never "2 of 3" for 2 of 5 (A4; V6IS-6).
  useEffect(() => { if (pending && showsReceipt(view, pending)) received(view, pending, { read: true }); }, [view]); // eslint-disable-line react-hooks/exhaustive-deps
  const receive = async () => {
    if (busy || body.length === 0 || unread) return;
    setBusy(true);
    setError(null);
    const expect = Object.fromEntries(waiting.filter((l) => (ticks[l.id] ?? 0) > 0).map((l) => [l.id, l.quantityReceived + ticks[l.id]!]));
    const sent: PendingReceipt = { bodyText, key, expect, ticks, ticked, inTransit, landed: false };
    try {
      received((await transfers.receive(view.id, body, key)).data, sent);
    } catch (err) {
      if (carriedOut(err)) {
        // Received, its answer not kept: the transfer read again — a read that shows the receipt, never the device's own copy of the
        // transfer as it was before it (D13). Not so read: said so (UC-INVENTORY-012 A4; TC-INVENTORY-223), the receipt kept as it was
        // sent and the panel offering only to read the transfer again (TC-INVENTORY-233).
        keep({ ...sent, landed: true });
        const v = await readShowing(view.id, (x) => showsReceipt(x, sent));
        if (v) received(v, sent);
        else onReceived(null, <><b>Received:</b> {UNREAD_RECEIPT}</>);
        return;
      }
      // Its answer lost, or still being received: the receipt and its key kept on the device, and the transfer no longer what the page
      // holds of it — opened again, it is read again (A4 step 3; TC-INVENTORY-234).
      if ((isNetworkError(err) && !err.offline) || (isApiError(err) && err.code === "REQUEST_IN_FLIGHT")) { keep(sent); onUnread(); }
      setError(err);
    } finally { setBusy(false); }
  };
  /** A4: the transfer read again after a receipt carried out unread — as it stands, the receipt in it; the panel then takes the next. */
  const readAgain = async () => {
    if (busy || !pending) return;
    setBusy(true);
    setError(null);
    try {
      const v = (await transfers.get(view.id)).data;
      if (showsReceipt(v, pending)) received(v, pending);
      else setError(STALE_READ);
    } catch (err) { setError(err); }
    finally { setBusy(false); }
  };
  return (
    <Panel title="Receive" meta={unread ? `Received — read ${view.number} again to see what is still in transit` : `${inTransit} unit${inTransit === 1 ? "" : "s"} in transit from ${view.fromBranch.name} · at the cost carried from Send`}>
      {error !== null && (unread
        ? <Alert tone="crit"><b>Not read again.</b> {error === STALE_READ ? "The transfer could be read only as it was before the receipt — this device may be offline: read it again once it is back online." : describeError(error)}</Alert>
        : <Alert tone="crit"><b>Not received.</b> {isApiError(error) && error.code === "VALIDATION_FAILED" && error.errors[0] ? ({ not_on_transfer: "A unit is not on this transfer.", already_received: "A unit is already received.", over_received: "More units than are in transit." }[error.errors[0].code] ?? describeError(error)) : describeError(error)}</Alert>)}
      {unread && (
        <Alert tone="warn" role="status" actions={<button className="btn sm primary" type="button" disabled={!online || busy} title={title} onClick={() => void readAgain()}>{busy ? "Reading…" : "Read it again"}</button>}>
          <b>{ticked} unit{ticked === 1 ? " was" : "s were"} received as ticked below.</b> Read the transfer again before receiving more: what was received is not received twice.
        </Alert>
      )}
      {open && pending && !pending.landed && pending.bodyText === bodyText && error === null && (
        <Alert tone="warn" role="status"><b>The last receipt&#39;s answer did not arrive:</b> {pending.ticked} unit{pending.ticked === 1 ? " is" : "s are"} ticked as it was sent. Press Receive to send it again — it is received once.</Alert>
      )}
      {open && (
        <div className="stack" style={{ gap: 8 }}>
          <div className="row c gap-8">
            <div className="input w-scan">
              <Icon name="barcode" />
              <input ref={scanRef} aria-label="Scan a unit as it comes out of the parcel" placeholder="Scan a serial, a box's barcode or an SKU as it comes out of the parcel…" autoComplete="off" maxLength={100} value={scan} disabled={!online} title={title}
                onChange={(e) => setScan(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onScan(scan); } }} autoFocus />
            </div>
            <CameraScan testId="parcel-camera" disabled={!online} title={title} onCode={(code) => onScan(code)} onMiss={(words) => setMsg(words)} />
          </div>
          <div aria-live="polite">{msg && <div className="small">{msg}</div>}</div>
        </div>
      )}
      <div className="tbl-scroll">
        <table>
          <thead><tr><th>Product</th><th className="serial-col">Serial</th><th className="r">Units</th><th>Status</th><th className="r">Cost</th><th>Received now</th></tr></thead>
          <tbody>{view.lines.map((l) => (
            <tr key={l.id}>
              <td className="product">{l.productName}<div className="small faint mono">{l.sku}</div>{l.serialNumber && <div className="small faint serial-sub">Serial <span className="mono nw">{l.serialNumber}</span></div>}</td>
              <td className="mono nw serial-col">{l.serialNumber ?? <span className="faint">—</span>}</td>
              <td className="r num">{l.quantity}</td>
              <td>{l.quantityReceived >= l.quantity ? <span className="chip ok"><span className="dot" />Received</span> : l.quantityInTransit > 0 ? <><SerialStatusChip status="InTransit" />{l.quantityReceived > 0 && <div className="small faint">{l.quantityReceived} of {l.quantity} received</div>}</> : <span className="chip neutral">Back at {view.fromBranch.name}</span>}</td>
              <td className="money">{formatMoney(l.unitCostCents)}</td>
              <td>{l.quantityInTransit === 0 ? <span className="faint">—</span> : l.serialId
                ? <Checkbox ariaLabel={`Received ${l.serialNumber}`} checked={(ticks[l.id] ?? 0) > 0} disabled={!open} onChange={(v) => setTicks((t) => ({ ...t, [l.id]: v ? 1 : 0 }))}>{(ticks[l.id] ?? 0) > 0 ? "ticked" : ""}</Checkbox>
                : <TextInput aria-label={`Received of ${l.sku}`} boxClass="w-80" inputMode="numeric" value={String(ticks[l.id] ?? 0)} disabled={!open} onChange={(e) => { const n = clampTo(l, e.target.value); setTicks((t) => ({ ...t, [l.id]: n })); }} onKeyDown={receivedKeys(l)} />}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      {open && (
        <div className="row c gap-8">
          <button className="btn primary" type="button" disabled={!online || busy || ticked === 0} title={title} onClick={() => void receive()}>{busy ? "Receiving…" : ticked === inTransit ? "Receive" : `Receive ${ticked} of ${inTransit}`}</button>
          <span className="help">Each unit leaves {view.fromBranch.name}&#39;s on hand and joins this branch&#39;s store at its carried cost; the rest stays in transit until received or cancelled.</span>
        </div>
      )}
    </Panel>
  );
}
